<script setup lang="ts">
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import { createPopper, Placement } from "@popperjs/core";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps<{
  placement?: Placement;
  content?: string;
  widthClass?: string;
}>();

const emit = defineEmits(["show", "hide"]);

const reference = ref<HTMLElement | null>(null);
const popper = ref<HTMLElement | null>(null);
const popperInstance = ref<ReturnType<typeof createPopper> | null>(null);
const showTooltip = ref(false);

const toggleTooltip = (value: boolean) => {
  showTooltip.value = value;
  emit(value ? "show" : "hide");
};

onMounted(() => {
  if (reference.value && popper.value) {
    popperInstance.value = createPopper(reference.value, popper.value, {
      placement: props.placement || "top",
      modifiers: [
        { name: "offset", options: { offset: [0, 8] } },
        { name: "preventOverflow", options: { padding: 8 } },
        { name: "arrow", options: { element: ".tooltip-arrow" } },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["top", "bottom", "left", "right"],
          },
        },
      ],
    });
  }
});

watch(showTooltip, () => {
  if (popperInstance.value) {
    popperInstance.value.update();
  }
});

onBeforeUnmount(() => {
  if (popperInstance.value) {
    popperInstance.value.destroy();
    popperInstance.value = null;
  }
});
</script>

<template>
  <div class="relative inline-block w-full" :class="widthClass">
    <!-- Trigger -->
    <div
      ref="reference"
      class="cursor-pointer inline-flex items-center"
      :class="widthClass"
      @mouseenter="toggleTooltip(true)"
      @mouseleave="toggleTooltip(false)"
    >
      <slot name="trigger">
        <InformationCircleIcon class="w-5 h-5 text-gray-500" />
      </slot>
    </div>

    <!-- Tooltip -->
    <transition name="fade">
      <Teleport to="body">
        <div
          v-show="showTooltip"
          ref="popper"
          class="tooltip-box z-50 bg-white text-black text-sm p-2 rounded shadow-xl border relative"
          role="tooltip"
        >
          <slot>
            {{ content }}
          </slot>
          <div class="tooltip-arrow" data-popper-arrow></div>
        </div>
      </Teleport>
    </transition>
  </div>
</template>

<style scoped>
.tooltip-box {
  white-space: nowrap;
  position: relative;
  border-right: 5px solid #00b087;
}

.tooltip-box[data-popper-placement^="left"] {
  border-right: none;
  border-left: 5px solid #00b087;
}

.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  z-index: 1;
}

/* Arrow for top placement */
.tooltip-box[data-popper-placement^="top"] > .tooltip-arrow {
  bottom: -5px;
  left: calc(50% - 5px);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid white;
}

/* Arrow for bottom placement */
.tooltip-box[data-popper-placement^="bottom"] > .tooltip-arrow {
  top: -5px;
  left: calc(50% - 5px);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 5px solid white;
}

/* Arrow for left placement */
.tooltip-box[data-popper-placement^="left"] > .tooltip-arrow {
  right: -5px;
  top: calc(50% - 5px);
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 5px solid white;
}

/* Arrow for right placement */
.tooltip-box[data-popper-placement^="right"] > .tooltip-arrow {
  left: -5px;
  top: calc(50% - 5px);
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-right: 5px solid white;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
